import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { UnifiedAIKeyService } from '../../../features/Settings/services/unified-ai-key.service';
import { DashboardTrendVoiceActionService } from '../../../features/Dashboard/services/dashboard-trend-voice-action.service';
import { SmartVideoVoiceActionService } from '../../../features/VideoGen/services/smart-video-voice-action.service';
import { SocialPostVoiceActionService } from '../../../features/Social/services/social-post-voice-action.service';
import { NavigationVoiceActionService } from '../../../features/VoiceAssistant/navigation/navigation-voice-action.service';
import { DialogService } from '../../dialogs/dialog.service';
import {
  AssistantAction,
  VoiceActionRunResult,
  assistantActionIds
} from './models';
import { readActionPayloadValue, readBooleanPayload } from './voice-action-payload';

interface ByokProbeResult {
  pipeline: string;
  category: string;
  provider: string;
  success: boolean;
  status?: string;
  message?: string;
}

@Injectable({
  providedIn: 'root'
})
export class VoiceActionRunnerService {
  private readonly router = inject(Router);
  private readonly byokService = inject(UnifiedAIKeyService);
  private readonly dashboardTrendActions = inject(DashboardTrendVoiceActionService);
  private readonly smartVideoActions = inject(SmartVideoVoiceActionService);
  private readonly socialPostActions = inject(SocialPostVoiceActionService);
  private readonly navigationActions = inject(NavigationVoiceActionService);
  private readonly dialogService = inject(DialogService);

  private readonly supportedActions = new Set<string>(assistantActionIds);

  async runActions(actions: AssistantAction[]): Promise<VoiceActionRunResult> {
    const result: VoiceActionRunResult = {
      handled: false,
      executedActionIds: [],
      unsupportedActionIds: [],
      errors: []
    };

    const spokenTextParts: string[] = [];

    for (const action of actions || []) {
      if (!this.supportedActions.has(action.id)) {
        result.unsupportedActionIds.push(action.id);
        continue;
      }

      try {
        if (action.id === 'navigate_route') {
          await this.navigationActions.open(action);
          result.executedActionIds.push(action.id);
          spokenTextParts.push(action.label ? `${action.label} opened.` : 'Opened.');
        } else if (action.id === 'open_template_studio_video') {
          const openCreate = this.readBooleanPayload(action, 'openCreate')
            ?? this.readBooleanPayload(action, 'createNew')
            ?? this.readBooleanPayload(action, 'openCreator')
            ?? false;

          await this.router.navigate(['/social', 'island'], {
            queryParams: {
              tab: 'templates',
              templateType: 'video',
              ...(openCreate ? { create: 'template', voiceAction: Date.now() } : {})
            }
          });
          result.executedActionIds.push(action.id);
          spokenTextParts.push(openCreate ? 'Video template creator opened.' : 'Template Studio opened.');
        } else if (action.id === 'open_smart_video_create') {
          await this.smartVideoActions.openCreate(action);
          result.executedActionIds.push(action.id);
          spokenTextParts.push('Smart Video opened.');
        } else if (action.id === 'open_processing_jobs') {
          await this.router.navigate(['/post-gen', 'jobs']);
          result.executedActionIds.push(action.id);
          spokenTextParts.push('Processing jobs opened.');
        } else if (action.id === 'open_review_pipeline') {
          const reviewPipeline = this.extractReviewPipeline(action);
          await this.router.navigate(['/review'], { queryParams: { pipeline: reviewPipeline } });
          result.executedActionIds.push(action.id);
          spokenTextParts.push('Review pipeline opened.');
        } else if (action.id === 'open_trends') {
          await this.smartVideoActions.openCreate({
            ...action,
            payload: {
              ...action.payload,
              source: 'trend-radar',
            },
          });
          result.executedActionIds.push(action.id);
          spokenTextParts.push('Smart Video opened for trend ideas.');
        } else if (action.id === 'open_trend_create') {
          await this.dashboardTrendActions.openTrendCreate(action);
          result.executedActionIds.push(action.id);
          spokenTextParts.push('Trend idea opened.');
        } else if (action.id === 'open_social_post_create') {
          await this.socialPostActions.openCreate(action);
          result.executedActionIds.push(action.id);
          spokenTextParts.push('Post Generation opened.');
        } else if (action.id === 'smart-video:compose') {
          const utterance = this.extractSmartVideoComposeUtterance(action);
          if (!utterance) {
            result.errors.push('smart-video:compose: Missing idea');
            continue;
          }
          window.dispatchEvent(new CustomEvent('voice-actions:smart-video:compose', {
            detail: { utterance }
          }));
          result.executedActionIds.push(action.id);
          spokenTextParts.push('Smart Video brief filled.');
        } else if (action.id === 'byok_test_keys') {
          if (this.openByokValidationDialog()) {
            spokenTextParts.push('AI keys की validation report खोल रहा हूं.');
          } else {
            const testSummary = await this.testAllKeys();
            spokenTextParts.push(testSummary);
          }
          result.executedActionIds.push(action.id);
        } else if (action.id === 'close_active_dialog') {
          if (this.closeActiveDialog()) {
            result.executedActionIds.push(action.id);
            spokenTextParts.push('Popup बंद कर दिया.');
          }
        } else if (action.id === 'explain_current_workflow') {
          spokenTextParts.push(action.label || 'I can explain this workflow and guide you one step at a time.');
          result.executedActionIds.push(action.id);
        } else if (action.id === 'cancel_guided_workflow') {
          spokenTextParts.push(action.label || 'Guided workflow cancelled.');
          result.executedActionIds.push(action.id);
        } else if (action.id === 'confirm_generate_smart_video') {
          await this.triggerSmartVideoGenerate();
          spokenTextParts.push('Smart Video generation command sent.');
          result.executedActionIds.push(action.id);
        }

        result.handled = true;
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Unknown action failure';
        result.errors.push(`${action.id}: ${message}`);
      }
    }

    if (spokenTextParts.length) {
      result.spokenText = spokenTextParts.join(' ');
    }

    return result;
  }

  private extractReviewPipeline(action: AssistantAction): 'Processing' | 'SmartVideo' | 'SocialPost' {
    const candidate = readActionPayloadValue<string>(action, 'pipeline');
    if (candidate === 'SmartVideo' || candidate === 'SocialPost' || candidate === 'Processing') {
      return candidate;
    }
    return 'Processing';
  }

  private async testAllKeys(): Promise<string> {
    try {
      const results = await firstValueFrom<any[]>(this.byokService.testAllKeys());
      const normalized = Array.isArray(results)
        ? results.map(this.normalizeByokResult.bind(this))
        : [];

      const total = normalized.length;
      if (!total) return 'BYOK key check did not return any test results.';

      const passed = normalized.filter((r) => r.success);
      const failed = normalized.filter((r) => !r.success);
      const failedShort = failed
        .map((r) => this.formatProbeLabel(r))
        .slice(0, 4)
        .join(', ');
      const configuredOnly = normalized.filter((r) => r.success && r.status === 'configured-only').length;

      if (!failed.length) {
        if (configuredOnly > 0) {
          return `BYOK key check passed. ${configuredOnly} providers are configured-only and could not run generation tests.`;
        }
        return 'BYOK key check passed for all providers.';
      }

      const missing = failed.filter((r) => r.status === 'missing');
      const failedButConfigured = failed.filter((r) => r.status !== 'missing');
      const missingList = missing.map((r) => this.formatProbeLabel(r)).join(', ');
      const failedList = failedButConfigured.map((r) => this.formatProbeLabel(r)).join(', ');

      const parts: string[] = [];
      if (missingList) {
        parts.push(`Missing providers: ${missingList}.`);
      }
      if (failedList) {
        parts.push(`Failed providers: ${failedList}.`);
      }
      if (!parts.length) {
        parts.push(`Failed providers: ${failedShort || 'unspecified checks failed'}.`);
      }

      return `BYOK key check incomplete. ${parts.join(' ')} Passed ${passed.length} and flagged ${failed.length}.`;
    } catch (error) {
      console.error('BYOK key check failed:', error);
      return 'BYOK key check failed due a network or server error.';
    }
  }

  private async triggerSmartVideoGenerate() {
    if (!this.router.url.startsWith('/video-gen/generator')) {
      await this.router.navigate(['/video-gen', 'generator'], {
        queryParams: { from: 'voice-assistant' }
      });
      window.setTimeout(() => {
        window.dispatchEvent(new CustomEvent('voice-actions:smart-video:generate'));
      }, 350);
      return;
    }

    window.dispatchEvent(new CustomEvent('voice-actions:smart-video:generate'));
  }

  private extractSmartVideoComposeUtterance(action: AssistantAction): string {
    const payloadKeys = ['utterance', 'idea', 'topic', 'inputData', 'prompt', 'script'];
    for (const key of payloadKeys) {
      const value = readActionPayloadValue<string>(action, key);
      if (typeof value === 'string' && value.trim()) {
        return value.trim();
      }
    }

    const label = action.label?.trim() || '';
    return label && !label.toLowerCase().includes('compose video brief') ? label : '';
  }

  private openByokValidationDialog(): boolean {
    const event = new CustomEvent('voice-actions:byok-test-keys', { cancelable: true });
    return !window.dispatchEvent(event);
  }

  private closeActiveDialog(): boolean {
    const event = new CustomEvent('voice-actions:close-active-dialog', { cancelable: true });
    if (!window.dispatchEvent(event)) {
      return true;
    }

    return this.dialogService.closeLatest();
  }

  private normalizeByokResult(raw: unknown): ByokProbeResult {
    const candidate = raw as ByokProbeResult;
    return {
      pipeline: candidate?.pipeline || 'pipeline',
      category: candidate?.category || 'category',
      provider: candidate?.provider || 'provider',
      success: Boolean(candidate?.success),
      status: candidate?.status,
      message: candidate?.message
    };
  }

  private formatProbeLabel(item: ByokProbeResult): string {
    return `${item.pipeline}/${item.category}/${item.provider}`;
  }

  private readActionPayloadValue<T>(action: AssistantAction, key: string): T | undefined {
    const payload = action.payload as Record<string, unknown> | undefined;
    const value = payload ? payload[key] : undefined;

    if (value === undefined || value === null) return undefined;
    return value as T;
  }

  private readNumberPayload(action: AssistantAction, key: string): number | undefined {
    const value = this.readActionPayloadValue<number | string>(action, key);
    if (value === undefined || value === null || value === '') return undefined;

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  private readBooleanPayload(action: AssistantAction, key: string): boolean | undefined {
    return readBooleanPayload(action, key);
  }
}
